/**
 * Suggestions in the web editors (plan 7.4): while the panel edits a file
 * that has pending suggestions, a strip at the foot of the editor walks
 * through them (Suggestion 1 of 3, Previous and Next), shows the words now
 * and the suggested words, and applies or dismisses the one on screen. The
 * editor reveals its target and draws the mark beside that block.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MaterialSymbol } from '../../icons/MaterialSymbol';
import { applySuggestions, dismissSuggestion } from './suggestions-controller';
import type { SuggestionItemRef } from './suggestions-store';
import { useSuggestionsDeps } from './useSuggestionsDeps';

export function SuggestionStrip({
  items,
  onReveal,
}: {
  items: readonly SuggestionItemRef[];
  /** Scroll to and mark the target of the suggestion on screen. */
  onReveal: (target: string) => void;
}) {
  const { t } = useTranslation('common');
  const deps = useSuggestionsDeps();
  const [index, setIndex] = useState(0);
  const current = items[Math.min(index, items.length - 1)];
  const target = current?.item.target;

  useEffect(() => {
    if (target) onReveal(target);
    // Reveal each suggestion once as it comes on screen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target]);

  if (!current) return null;
  const at = Math.min(index, items.length - 1);
  return (
    <section className="tabbio-suggestion-strip" aria-label={t('chat.artifacts.suggestions.title')}>
      <div className="tabbio-suggestion-strip-head">
        <MaterialSymbol name="lightbulb" size={18} />
        <span className="tabbio-suggestion-strip-title">
          {t('chat.artifacts.suggestions.position', { current: at + 1, total: items.length })}
        </span>
        <button
          type="button"
          className="tabbio-panel-icon-button is-small"
          disabled={at === 0}
          onClick={() => setIndex(at - 1)}
          aria-label={t('chat.artifacts.suggestions.previous')}
          title={t('chat.artifacts.suggestions.previous')}
        >
          <MaterialSymbol name="chevron_left" size={18} className="tabbio-mirror-rtl" />
        </button>
        <button
          type="button"
          className="tabbio-panel-icon-button is-small"
          disabled={at >= items.length - 1}
          onClick={() => setIndex(at + 1)}
          aria-label={t('chat.artifacts.suggestions.next')}
          title={t('chat.artifacts.suggestions.next')}
        >
          <MaterialSymbol name="chevron_right" size={18} className="tabbio-mirror-rtl" />
        </button>
      </div>
      {current.item.reason ? (
        <p className="tabbio-suggestion-reason" dir="auto">
          {current.item.reason}
        </p>
      ) : null}
      <div className="tabbio-suggestion-diff">
        {current.item.before ? (
          <div className="tabbio-suggestion-before">
            <span>{t('chat.artifacts.suggestions.before')}</span>
            <p dir="auto">{current.item.before}</p>
          </div>
        ) : null}
        <div className="tabbio-suggestion-after">
          <span>{t('chat.artifacts.suggestions.after')}</span>
          <p dir="auto">{current.item.after}</p>
        </div>
      </div>
      <div className="tabbio-suggestion-actions">
        <button type="button" className="tabbio-text-button" onClick={() => dismissSuggestion(current.key, current.item.index)}>
          {t('chat.artifacts.suggestions.dismiss')}
        </button>
        <button
          type="button"
          className="tabbio-panel-primary is-small"
          onClick={() => void applySuggestions(current.key, [current.item.index], deps)}
        >
          {t('chat.artifacts.suggestions.apply')}
        </button>
      </div>
    </section>
  );
}
